import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom, of } from 'rxjs';
import { catchError, map, filter } from 'rxjs/operators';
import { SelectItem } from '@mydocma/dynamic-form/lib/fields/filter-select/select-item';
import { User, Users } from '@docma-mm-core/user/user';
import { FieldsFilterApiService } from './fields-filter-api.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthService, User as AuthUser } from '@mydocma/auth';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MmDbService } from '../../mm-db/mm-db-service';
import { MmProjectDbCollections } from '../../mm-db/mm-project-db-schema';
import { MmViewService } from '../../mm-view.service';
import { Logger } from '@mydocma/common/src/lib/logger';

interface UserCache {
  projectId: number;
  users: SelectItem[];
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
@UntilDestroy()
export class FieldsFilterDataService {
  private static readonly CACHE_DURATION = 5 * 60 * 1000;
  
  private userCacheSubject = new BehaviorSubject<UserCache | null>(null);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  
  public userCache$ = this.userCacheSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private readonly apiService: FieldsFilterApiService,
    private readonly translateService: TranslateService,
    private readonly authService: AuthService,
    private readonly mmDbService: MmDbService,
    private readonly mmViewService: MmViewService,
    private readonly logger: Logger
  ) {
    this.translateService.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const currentCache = this.userCacheSubject.value;
        if (!currentCache || !currentCache.users?.length) {
          return;
        }

        const updatedUsers = currentCache.users.map(u => {
          const isMe = (u as any)?.data?.isMe === true;
          const fullName = (u as any)?.data?.fullName;
          if (isMe && fullName) {
            return {
              ...u,
              text: `${this.translateService.instant('MM.fields.filter.userOptions.me')} (${fullName})`
            };
          }
          return u;
        });

        this.userCacheSubject.next({
          ...currentCache,
          users: updatedUsers,
          timestamp: Date.now()
        });
      });
  }


  async loadProjectUsers(projectId: number, forceReload: boolean = false): Promise<SelectItem[]> {
    const currentCache = this.userCacheSubject.value;
    
    if (!forceReload && currentCache && 
        currentCache.projectId === projectId &&
        (Date.now() - currentCache.timestamp) < FieldsFilterDataService.CACHE_DURATION) {
      return currentCache.users;
    }

    this.loadingSubject.next(true);
    
    try {
      
      const response = await firstValueFrom(
        this.apiService.getProjectUsers(projectId).pipe(
          catchError(error => {
            this.logger.error(error, '', 'FieldsFilterDataService');
            return of({ Count: 0, Data: [] } as Users);
          })
        )
      );
      
      const selectItems = await this.formatUsersForDropdown(response?.Data || []);
      
      this.userCacheSubject.next({
        projectId,
        users: selectItems,
        timestamp: Date.now()
      });
      
      return selectItems;
    } finally {
      this.loadingSubject.next(false);
    }
  }


  private async formatUsersForDropdown(users: User[]): Promise<SelectItem[]> {
    const currentUser = await firstValueFrom(
      this.authService.user.pipe(
        map(user => user as AuthUser)
      )
    );
    
    if (!currentUser) {
      console.warn('No current user found');
      return [];
    }

    const currentUserName = `${currentUser.firstName} ${currentUser.lastName}`.trim();
    const selectItems: SelectItem[] = [];
    
    let currentUserNumericalId: string | null = null;
    let currentUserInProject: User | undefined;
    
    try {
      const currentProject = await firstValueFrom(this.mmViewService.currentProject$.pipe(filter(p => !!p)));
      if (currentProject?.MyDocmaUid) {
        const db = await this.mmDbService.getProjectDb(currentProject.MyDocmaUid);
        const dbUser = await db.get(MmProjectDbCollections.userPermissions as never, 'User') as User;
        
        if (dbUser?.Id) {
          currentUserNumericalId = dbUser.Id.toString();
        }
      }
    } catch (error) {
      this.logger.error(error, '', 'FieldsFilterDataService');
    }
    // if indexedDB did not return an ID 
    // see if we can get the current user's numerical ID from the list of users in the project
    if (!currentUserNumericalId) {
      // to do confirm that the 'currentUsers' id is mapped correctly to the user.uid from the project users list
      currentUserInProject = users.find(user => user.Uid === currentUser.id);
      
      if (currentUserInProject && currentUserInProject?.Id) {
        currentUserNumericalId = currentUserInProject.Id.toString();
      } 
    }
    let hasMe = false;
    if (currentUserNumericalId) {
      selectItems.push({
        id: currentUserNumericalId,
        text: `${this.translateService.instant('MM.fields.filter.userOptions.me')} (${currentUserName})`,
        data: { isMe: true, fullName: currentUserName }
      } as any);
      hasMe = true;
    } else {
      this.logger.error('Could not find numerical ID for current user, not adding "Me" user as it will not be accepted by the API without it', '', 'FieldsFilterDataService');
    }
    
    const otherUsers = users
      .filter(user => {
       if (!user.Id) {
          this.logger.error('[formatUsersForDropdown] User has no ID, skipping, api filter will fail for this user without it. user: ' + JSON.stringify({FirstName: user?.FirstName, LastName: user?.LastName, Uid: user?.Uid}), '', 'FieldsFilterDataService');
          return false;
        };
        // no need to filter out current user if 'me' is not added in the list
        if (!hasMe) return true;
        return user.Id.toString() !== currentUserNumericalId;
      })
      .map(user => ({
        id: user.Id.toString(),
        firstName: user.FirstName || '',
        lastName: user.LastName || '',
        fullName: `${user.FirstName || ''} ${user.LastName || ''}`.trim()
      }))
      .filter(user => user.fullName)
      .sort((a, b) => {
        const lastNameCompare = a.lastName.localeCompare(b.lastName);
        if (lastNameCompare !== 0) return lastNameCompare;
        return a.firstName.localeCompare(b.firstName);
      });
    
    otherUsers.forEach(user => {
      selectItems.push({
        id: user.id,
        text: user.fullName,
        data: { isMe: false, fullName: user.fullName }
      } as any);
    });
    
    return selectItems;
  }


  getUserDisplayNameById(userId: string, projectId: number): Observable<string> {
    return this.userCache$.pipe(
      map(cache => {
        if (!cache || cache.projectId !== projectId) {
          throw new Error('[getUserDisplayNameById] No user cache found for projectId: ' + projectId + '. Cache must be set before calling this function. Getting display name for user failed: ' + userId);
        }

        const user = cache.users.find(u => u.id === userId);

        if (!user) {
          throw new Error('[getUserDisplayNameById] No user found in cache for userId: ' + userId);
        }

        const mePrefix = this.translateService.instant('MM.fields.filter.userOptions.me') + ' (';
        if (user.text.startsWith(mePrefix)) {
          const match = user.text.match(/\(([^)]+)\)/);
          return match ? match[1] : user.text;
        }
        return user.text;

      })
    );
  }

  clearCache(projectId?: number): void {
    if (projectId) {
      const currentCache = this.userCacheSubject.value;
      if (currentCache && currentCache.projectId === projectId) {
        this.userCacheSubject.next(null);
      }
    } else {
      this.userCacheSubject.next(null);
    }
  }
}